import { Injectable } from '@angular/core';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { Observable } from 'rxjs';

import {
  GridService,
  FormatService,
  IdDescription,
  LanguageService,
  MenuService,
  UtilityService,
  GridFieldOptions,
  DateFormatService,
} from 'tms-lib';
import {
  LocationSelectRendererComponent,
  ProductQuantityRendererComponent,
  ProductRendererComponent,
  ProductSearchRendererComponent,
} from '../../components';

@Injectable({
  providedIn: 'root',
})
export class TmsWebGridService extends GridService {
  constructor(
    protected formatService: FormatService,
    protected languageService: LanguageService,
    protected menuService: MenuService,
    protected dateFormatService: DateFormatService,
    protected utilityService: UtilityService,
    private gridService: GridService,
  ) {
    super(formatService, languageService, menuService, dateFormatService, utilityService);
  }

  public load() {
    this.gridService.components = {
      ...this.components,
      locationSelectRenderer: LocationSelectRendererComponent,
      productRenderer: ProductRendererComponent,
      productQuantityRenderer: ProductQuantityRendererComponent,
      productSearchRenderer: ProductSearchRendererComponent,
    };
  }

  locationSelect(
    field: string,
    headerName: string,
    selectOptions?: Observable<IdDescription[]> | IdDescription[],
    options?: GridFieldOptions,
  ): ColDef {
    if (this.menuService.isReadOnly) {
      return this.text(field, headerName, options);
    }

    return {
      ...this.text(field, headerName, options),
      cellRenderer: 'locationSelectRenderer',
      cellRendererParams: {
        context: { options: this.utilityService.mapSelectOptions(selectOptions) },
      } as ICellRendererParams,
    };
  }

  product(
    keyField: string,
    descriptionField: string,
    productTypeField: string,
    headerName: string,
    options?: GridFieldOptions,
  ): ColDef {
    return {
      ...this.text(keyField, headerName, options),
      cellRenderer: 'productRenderer',
      cellRendererParams: {
        descriptionField,
        productTypeField,
      },
      width: 300,
      filter: false,
      sortable: false,
      suppressAutoSize: true,
      suppressColumnsToolPanel: true,
      ...options,
    };
  }

  productQuantity(
    quantityField: string,
    productTypeField: string,
    unitOfMeasureField: string,
    priceField: string,
    currencyField: string,
    headerName: string,
    update: (model: any) => void,
    add: (model: any) => void,
    minus: (model: any) => void,
    remove: (model: any) => void,
    options?: GridFieldOptions,
  ): ColDef {
    return {
      ...this.text(quantityField, headerName, options),
      cellRenderer: 'productQuantityRenderer',
      cellRendererParams: {
        productTypeField,
        unitOfMeasureField,
        priceField,
        currencyField,
        update,
        add,
        minus,
        remove,
      },
      filter: false,
      sortable: false,
      suppressAutoSize: true,
      suppressColumnsToolPanel: true,
      ...options,
    };
  }

  productSearch(selected?: (model: any) => Observable<any>, show?: (data: any) => boolean): ColDef {
    const field = 'productSearch';
    const headerName = $localize`Product Search`;
    return {
      ...this.text(field, headerName),
      cellRenderer: 'productSearchRenderer',
      cellRendererParams: {
        selected,
        show,
      },
    };
  }
}
